(() => {
  const selectors = {
    root: "[data-crystal-reveal]",
    layer: "[data-ce-layer]",
    dialog: "[data-ce-dialog]",
    close: "[data-ce-close]",
    launcher: "[data-ce-launcher]",
    scratch: "[data-ce-scratch]",
    canvas: "[data-ce-canvas]",
    hint: "[data-ce-scratch-hint]",
    claim: "[data-ce-claim]",
    submit: "[data-ce-submit]",
    success: "[data-ce-success]",
    copy: "[data-ce-copy]"
  };

  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");

  const hoursToMs = (hours) => Number(hours || 0) * 60 * 60 * 1000;

  function readState(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key)) || {};
    } catch (_) {
      return {};
    }
  }

  function writeState(key, patch) {
    const next = { ...readState(key), ...patch };
    try {
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch (_) {}
    return next;
  }

  function emit(root, name, detail = {}) {
    const payload = {
      experienceId: root.dataset.experienceId,
      variant: root.dataset.variant,
      ...detail
    };
    window.dispatchEvent(new CustomEvent(`crystal:popup:${name}`, { detail: payload }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `crystal_popup_${name}`, ...payload });
    }
  }

  function anotherOverlayIsOpen(root) {
    if (document.body.classList.contains("modal-open") || document.body.classList.contains("overflow-hidden")) {
      return true;
    }
    return [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some((dialog) => {
      return !root.contains(dialog) && !dialog.hidden && dialog.offsetParent !== null;
    });
  }

  function initScratch(root, reveal) {
    const canvas = root.querySelector(selectors.canvas);
    const scratch = root.querySelector(selectors.scratch);
    if (!canvas || !scratch) return;

    const context = canvas.getContext("2d", { willReadFrequently: false });
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const cells = new Set();
    const gridColumns = 20;
    const gridRows = 16;
    const threshold = 0.62;
    let drawing = false;
    let started = false;
    let revealed = false;
    let lastPoint = null;

    function paintFallback(width, height) {
      const gradient = context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "#fff8fb");
      gradient.addColorStop(0.28, "#f7bfd0");
      gradient.addColorStop(0.56, "#fffdfd");
      gradient.addColorStop(0.78, "#ee91b0");
      gradient.addColorStop(1, "#f9dce6");
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);
      context.globalAlpha = 0.24;
      for (let index = 0; index < 36; index += 1) {
        const x = (index * 97) % width;
        const y = (index * 53) % height;
        context.fillStyle = index % 2 ? "#ffffff" : "#d986aa";
        context.beginPath();
        context.arc(x, y, 4 + (index % 8), 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;
    }

    function drawTexture() {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      canvas.width = Math.round(rect.width * scale);
      canvas.height = Math.round(rect.height * scale);
      context.setTransform(1, 0, 0, 1, 0, 0);
      paintFallback(canvas.width, canvas.height);

      const image = new Image();
      image.onload = () => {
        const imageRatio = image.width / image.height;
        const canvasRatio = canvas.width / canvas.height;
        let sourceWidth = image.width;
        let sourceHeight = image.height;
        let sourceX = 0;
        let sourceY = 0;
        if (imageRatio > canvasRatio) {
          sourceWidth = image.height * canvasRatio;
          sourceX = (image.width - sourceWidth) / 2;
        } else {
          sourceHeight = image.width / canvasRatio;
          sourceY = (image.height - sourceHeight) / 2;
        }
        context.globalCompositeOperation = "source-over";
        context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height);
      };
      image.src = canvas.dataset.textureUrl;
    }

    function pointFromEvent(event) {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) * (canvas.width / rect.width),
        y: (event.clientY - rect.top) * (canvas.height / rect.height),
        displayX: event.clientX - rect.left,
        displayY: event.clientY - rect.top,
        displayWidth: rect.width,
        displayHeight: rect.height
      };
    }

    function markCoverage(point) {
      const column = Math.max(0, Math.min(gridColumns - 1, Math.floor((point.displayX / point.displayWidth) * gridColumns)));
      const row = Math.max(0, Math.min(gridRows - 1, Math.floor((point.displayY / point.displayHeight) * gridRows)));
      const radius = 1;
      for (let x = column - radius; x <= column + radius; x += 1) {
        for (let y = row - radius; y <= row + radius; y += 1) {
          if (x >= 0 && x < gridColumns && y >= 0 && y < gridRows) cells.add(`${x}:${y}`);
        }
      }
      if (cells.size / (gridColumns * gridRows) >= threshold) completeReveal();
    }

    function scratchLine(point) {
      if (!lastPoint) lastPoint = point;
      context.globalCompositeOperation = "destination-out";
      context.lineCap = "round";
      context.lineJoin = "round";
      context.lineWidth = Math.max(26 * scale, canvas.width * 0.078);
      context.beginPath();
      context.moveTo(lastPoint.x, lastPoint.y);
      context.lineTo(point.x, point.y);
      context.stroke();
      lastPoint = point;
      markCoverage(point);
    }

    function begin(event) {
      if (revealed) return;
      event.preventDefault();
      drawing = true;
      lastPoint = null;
      canvas.setPointerCapture?.(event.pointerId);
      if (!started) {
        started = true;
        scratch.classList.add("is-started");
        emit(root, "scratch_start");
      }
      scratchLine(pointFromEvent(event));
    }

    function move(event) {
      if (!drawing || revealed) return;
      event.preventDefault();
      scratchLine(pointFromEvent(event));
    }

    function end(event) {
      drawing = false;
      lastPoint = null;
      if (event.pointerId !== undefined) canvas.releasePointerCapture?.(event.pointerId);
    }

    function completeReveal() {
      if (revealed) return;
      revealed = true;
      started = true;
      scratch.classList.add("is-started");
      scratch.classList.add("is-revealed");
      window.setTimeout(reveal, 210);
    }

    canvas.addEventListener("pointerdown", begin);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", end);
    canvas.addEventListener("pointercancel", end);
    canvas.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        completeReveal();
      }
    });

    drawTexture();
    root.addEventListener("crystal:resize-canvas", drawTexture);

    if (root.querySelector("[data-ce-form-error='true']")) completeReveal();
  }

  function init(root) {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const layer = root.querySelector(selectors.layer);
    const dialog = root.querySelector(selectors.dialog);
    const launcher = root.querySelector(selectors.launcher);
    const claim = root.querySelector(selectors.claim);
    const submit = root.querySelector(selectors.submit);
    const success = root.querySelector(selectors.success);
    const copyButton = root.querySelector(selectors.copy);
    const stateKey = `crystal_popup_exp:${root.dataset.experienceId}`;
    const dismissCooldown = hoursToMs(root.dataset.dismissCooldownHours);
    const completeCooldown = hoursToMs(root.dataset.completeCooldownHours);
    const showLauncher = root.dataset.showLauncher === "true";
    const designMode = root.dataset.themeEditor === "true";
    let lastFocused = null;
    let isOpen = false;
    let hasRevealed = Boolean(success);

    writeState(stateKey, {
      experienceId: root.dataset.experienceId,
      variant: root.dataset.variant
    });
    emit(root, "view");

    function showLauncherButton() {
      if (launcher && showLauncher && !success) launcher.hidden = false;
    }

    function open(source = "automatic") {
      if (isOpen) return;
      lastFocused = document.activeElement;
      layer.hidden = false;
      layer.setAttribute("aria-hidden", "false");
      launcher.hidden = true;
      document.body.classList.add("crystal-reveal-open");
      root.classList.add("is-opening");
      isOpen = true;
      writeState(stateKey, { lastOpenedAt: Date.now() });
      emit(root, "open", { source });
      window.requestAnimationFrame(() => {
        dialog.focus({ preventScroll: true });
        root.dispatchEvent(new Event("crystal:resize-canvas"));
      });
      window.setTimeout(() => root.classList.remove("is-opening"), 900);
    }

    function close(reason = "dismiss") {
      if (!isOpen) return;
      layer.hidden = true;
      layer.setAttribute("aria-hidden", "true");
      document.body.classList.remove("crystal-reveal-open");
      isOpen = false;
      if (reason === "dismiss") {
        writeState(stateKey, { dismissedAt: Date.now() });
        emit(root, "dismiss", { revealed: hasRevealed });
      }
      showLauncherButton();
      lastFocused?.focus?.({ preventScroll: true });
    }

    function reveal() {
      if (hasRevealed || !claim) return;
      hasRevealed = true;
      claim.hidden = false;
      emit(root, "reveal");
      if (window.matchMedia("(min-width: 521px)").matches) {
        window.setTimeout(() => claim.querySelector("input")?.focus({ preventScroll: true }), 470);
      }
    }

    function scheduleOpen() {
      if (designMode || success || root.querySelector("[data-ce-form-error='true']")) {
        open(designMode ? "theme_editor" : "form_return");
        return;
      }

      const state = readState(stateKey);
      const now = Date.now();
      const dismissedRecently = state.dismissedAt && now - state.dismissedAt < dismissCooldown;
      const completedRecently = state.completedAt && now - state.completedAt < completeCooldown;
      if (dismissedRecently || completedRecently) {
        showLauncherButton();
        return;
      }

      const tryOpen = (attempt = 0) => {
        if (document.hidden) return;
        if (anotherOverlayIsOpen(root) && attempt < 4) {
          window.setTimeout(() => tryOpen(attempt + 1), 2500);
          return;
        }
        if (!anotherOverlayIsOpen(root)) open("automatic");
        else showLauncherButton();
      };
      window.setTimeout(tryOpen, Number(root.dataset.delaySeconds || 0) * 1000);
    }

    root.querySelectorAll(selectors.close).forEach((button) => {
      button.addEventListener("click", () => close("dismiss"));
    });
    launcher?.addEventListener("click", () => open("launcher"));

    document.addEventListener("keydown", (event) => {
      if (!isOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        close("dismiss");
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...dialog.querySelectorAll(focusableSelector)].filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    submit?.closest("form")?.addEventListener("submit", () => {
      writeState(stateKey, { submittedAt: Date.now() });
      emit(root, "submit");
    });

    if (success) {
      writeState(stateKey, { completedAt: Date.now() });
      emit(root, "complete");
    } else {
      initScratch(root, reveal);
    }

    copyButton?.addEventListener("click", async () => {
      const value = copyButton.dataset.copyValue || "";
      try {
        await navigator.clipboard.writeText(value);
      } catch (_) {
        const input = document.createElement("textarea");
        input.value = value;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      const state = copyButton.querySelector("[data-ce-copy-state]");
      if (state) {
        state.textContent = copyButton.dataset.copiedLabel;
        window.setTimeout(() => { state.textContent = copyButton.dataset.copyLabel; }, 1800);
      }
    });

    scheduleOpen();
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll(selectors.root).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
  document.addEventListener("shopify:section:select", (event) => {
    const root = event.target.querySelector?.(selectors.root);
    root?.querySelector(selectors.launcher)?.click();
  });
})();
